import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { UserCheck, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { secureApiRequest } from "@/lib/csrfClient";

interface ReassignDialogProps {
  type: "assessment" | "qbr";
  itemId: number;
  itemTitle: string;
  currentAssigneeId?: string;
  currentAssigneeName?: string;
  currentVcioId?: string | null;
  currentVcioName?: string | null;
  children: React.ReactNode;
}

export function ReassignDialog({
  type,
  itemId,
  itemTitle,
  currentAssigneeId,
  currentAssigneeName,
  currentVcioId,
  currentVcioName,
  children,
}: ReassignDialogProps) {
  const [open, setOpen] = useState(false);
  const [selectedAlignmentEngineerId, setSelectedAlignmentEngineerId] = useState<string>("");
  const [selectedVcioId, setSelectedVcioId] = useState<string>("");
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch users in organization
  const { data: users = [], isLoading: usersLoading } = useQuery<any[]>({
    queryKey: ["/api/users/list"],
    enabled: open,
  });

  // For assessments, filter users by role
  const alignmentEngineerUsers = type === "assessment" 
    ? users.filter((user: any) => 
        ['alignment_engineer', 'vcio', 'admin', 'owner'].includes(user.role)
      )
    : users;

  const vcioUsers = type === "assessment"
    ? users.filter((user: any) => 
        ['vcio', 'admin', 'owner'].includes(user.role)
      )
    : users;

  // Reassignment mutation
  const reassignMutation = useMutation({
    mutationFn: async (reassignmentData: { assignedToId?: string; vcioId?: string; newAssigneeId?: string }) => {
      const response = await secureApiRequest(`/api/${type}s/${itemId}/reassign`, {
        method: type === "assessment" ? "PATCH" : "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(reassignmentData),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Unable to connect to the server");
      }
      
      return response.json();
    },
    onSuccess: (_, variables) => {
      let description = "";
      
      if (type === "assessment") {
        const changedRoles: string[] = [];
        
        if (variables.assignedToId !== undefined) {
          const selectedUser = users.find((u: any) => u.id === variables.assignedToId);
          if (selectedUser) {
            changedRoles.push(`Alignment Engineer: ${selectedUser.firstName} ${selectedUser.lastName}`);
          }
        }
        
        if (variables.vcioId !== undefined) {
          const selectedUser = users.find((u: any) => u.id === variables.vcioId);
          if (selectedUser) {
            changedRoles.push(`vCIO: ${selectedUser.firstName} ${selectedUser.lastName}`);
          }
        }
        
        description = changedRoles.length > 0
          ? `Assessment "${itemTitle}" has been reassigned. ${changedRoles.join(", ")}`
          : `Assessment "${itemTitle}" has been updated.`;
      } else {
        const selectedUser = users.find((u: any) => u.id === variables.newAssigneeId);
        description = `Business Review "${itemTitle}" has been reassigned to ${selectedUser?.firstName} ${selectedUser?.lastName}.`;
      }
      
      toast({
        title: "Reassignment Successful",
        description,
      });
      
      // Invalidate and refetch relevant data
      queryClient.invalidateQueries({ queryKey: [`/api/${type}s`] });
      queryClient.invalidateQueries({ queryKey: [`/api/${type}s`, itemId] });
      
      setOpen(false);
      setSelectedAlignmentEngineerId("");
      setSelectedVcioId("");
    },
    onError: (error: any) => {
      toast({
        title: "Reassignment Failed",
        description: error.message || `Failed to reassign ${type}`,
        variant: "destructive",
      });
    },
  });

  const handleReassign = () => {
    if (type === "assessment") {
      // For assessments, allow independent reassignment of either role
      const hasAlignmentEngineerChange = selectedAlignmentEngineerId && selectedAlignmentEngineerId !== currentAssigneeId;
      const hasVcioChange = selectedVcioId && selectedVcioId !== currentVcioId;
      
      if (!hasAlignmentEngineerChange && !hasVcioChange) {
        toast({
          title: "No Changes",
          description: "Please select a different user for at least one role.",
          variant: "destructive",
        });
        return;
      }
      
      const reassignmentData: { assignedToId?: string; vcioId?: string } = {};
      
      if (hasAlignmentEngineerChange) {
        reassignmentData.assignedToId = selectedAlignmentEngineerId;
      }
      
      if (hasVcioChange) {
        reassignmentData.vcioId = selectedVcioId;
      }
      
      reassignMutation.mutate(reassignmentData);
    } else {
      // For Business Reviews, use the legacy single-user reassignment
      if (!selectedAlignmentEngineerId) {
        toast({
          title: "No User Selected",
          description: "Please select a user to assign this item to.",
          variant: "destructive",
        });
        return;
      }

      if (selectedAlignmentEngineerId === currentAssigneeId) {
        toast({
          title: "Same User Selected",
          description: "This item is already assigned to the selected user.",
          variant: "destructive",
        });
        return;
      }

      reassignMutation.mutate({ newAssigneeId: selectedAlignmentEngineerId });
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserCheck className="h-5 w-5" />
            Reassign {type === "assessment" ? "Assessment" : "Business Review"}
          </DialogTitle>
          <DialogDescription>
            Change the assigned user{type === "assessment" ? "s" : ""} for "{itemTitle}".
            {type === "assessment" ? (
              <div className="mt-2 space-y-1 text-sm text-muted-foreground">
                {currentAssigneeName && (
                  <div>
                    Current Alignment Engineer: <strong>{currentAssigneeName}</strong>
                  </div>
                )}
                {currentVcioName && (
                  <div>
                    Current vCIO: <strong>{currentVcioName}</strong>
                  </div>
                )}
              </div>
            ) : (
              currentAssigneeName && (
                <span className="block mt-2 text-sm text-muted-foreground">
                  Currently assigned to: <strong>{currentAssigneeName}</strong>
                </span>
              )
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          {usersLoading ? (
            <div className="flex items-center justify-center p-4">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="ml-2">Loading users...</span>
            </div>
          ) : type === "assessment" ? (
            <>
              <div className="grid gap-2">
                <Label htmlFor="alignment-engineer">Alignment Engineer</Label>
                <Select
                  value={selectedAlignmentEngineerId}
                  onValueChange={setSelectedAlignmentEngineerId}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={currentAssigneeName || "Select Alignment Engineer"} />
                  </SelectTrigger>
                  <SelectContent>
                    {alignmentEngineerUsers.map((user: any) => (
                      <SelectItem key={user.id} value={user.id}>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">
                            {user.firstName} {user.lastName}
                          </span>
                          <span className="text-sm text-muted-foreground">
                            ({user.role})
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="vcio">vCIO</Label>
                <Select
                  value={selectedVcioId}
                  onValueChange={setSelectedVcioId}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={currentVcioName || "Select vCIO"} />
                  </SelectTrigger>
                  <SelectContent>
                    {vcioUsers.map((user: any) => (
                      <SelectItem key={user.id} value={user.id}>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">
                            {user.firstName} {user.lastName}
                          </span>
                          <span className="text-sm text-muted-foreground">
                            ({user.role})
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </>
          ) : (
            <div className="grid gap-2">
              <Label htmlFor="assignee">Assign to</Label>
              <Select
                value={selectedAlignmentEngineerId}
                onValueChange={setSelectedAlignmentEngineerId}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a user" />
                </SelectTrigger>
                <SelectContent>
                  {users
                    .filter((user: any) => user.id !== currentAssigneeId)
                    .map((user: any) => (
                      <SelectItem key={user.id} value={user.id}>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">
                            {user.firstName} {user.lastName}
                          </span>
                          <span className="text-sm text-muted-foreground">
                            ({user.email})
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            onClick={() => {
              setOpen(false);
              setSelectedAlignmentEngineerId("");
              setSelectedVcioId("");
            }}
            disabled={reassignMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleReassign}
            disabled={reassignMutation.isPending}
          >
            {reassignMutation.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            Reassign
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}